import { useState } from 'react'
import { Badge } from 'react-bootstrap'
import { TwilioComplianceEmbed } from '@twilio/twilio-compliance-embed'
import { V1ApiIntegrationsClient } from 'core/http-common'
import toast from 'core/toast'
import { ITwilioAccountDto } from 'generated/v1/v1IntegrationsApi'
import MessageAlert from 'components/Alerts/MessageAlert/MessageAlert'
import { DefaultButton } from 'components'

interface TwilioComplianceEmbeddableProps {
    account: ITwilioAccountDto
    onStatusChange?: () => void
}

interface ComplianceSession {
    inquiryId: string
    sessionToken: string
}

const TwilioComplianceEmbeddable = ({ account, onStatusChange }: TwilioComplianceEmbeddableProps) => {
    const [loading, setLoading] = useState(false)
    const [sessionData, setSessionData] = useState<ComplianceSession | null>(null)
    const [showEmbed, setShowEmbed] = useState(false)

    const handleStartCompliance = async () => {
        if (!account.accountSid) return

        try {
            setLoading(true)
            const result = await V1ApiIntegrationsClient.initializeComplianceInquiry(account.accountSid)
            if (result.payload?.inquiryId && result.payload?.sessionToken) {
                setSessionData({
                    inquiryId: result.payload.inquiryId,
                    sessionToken: result.payload.sessionToken
                })
                setShowEmbed(true)
            }
        } catch (e) {
            toast.responseErrors(e)
        } finally {
            setLoading(false)
        }
    }

    const handleFormClose = () => {
        setShowEmbed(false)
        setSessionData(null)
        onStatusChange?.()
    }

    const getStatusVariant = (status: string | null | undefined): string => {
        if (!status) return 'secondary'
        const lowerStatus = status.toLowerCase()
        if (lowerStatus.includes('approved') || lowerStatus.includes('verified')) return 'success'
        if (lowerStatus.includes('rejected') || lowerStatus.includes('failed')) return 'danger'
        if (lowerStatus.includes('pending') || lowerStatus.includes('in-review')) return 'warning'
        return 'info'
    }

    const getButtonText = (status: string | null | undefined): string => {
        // If inquiry exists but not yet submitted, it's a resume
        if (!status || status === 'pending-compliance') {
            return account.complianceInquiryId ? 'Resume Compliance Form' : 'Start Compliance Form'
        }
        const lowerStatus = status.toLowerCase()
        if (lowerStatus.includes('rejected') || lowerStatus.includes('failed')) return 'Resubmit Compliance Form'
        if (lowerStatus.includes('pending') || lowerStatus.includes('in-review')) return 'Resume Compliance Form'
        if (lowerStatus.includes('approved') || lowerStatus.includes('verified')) return 'View Compliance Form'
        return 'Open Compliance Form'
    }

    const canEditCompliance = (status: string | null | undefined): boolean => {
        if (!status) return true
        const lowerStatus = status.toLowerCase()
        return !lowerStatus.includes('approved') && !lowerStatus.includes('verified')
    }

    return (
        <div className='twilio-compliance-embeddable'>
            {!showEmbed ? (
                <>
                    <div className='mb-4'>
                        <div className='d-flex align-items-center gap-2 mb-3'>
                            <strong>Compliance Status:</strong>
                            <Badge bg={getStatusVariant(account.twilioStatus)}>
                                {account.twilioStatus || 'Not Started'}
                            </Badge>
                        </div>
                        {account.complianceLastModifiedDate && (
                            <p className='text-muted mb-2'>
                                <small>Last updated: {new Date(account.complianceLastModifiedDate).toLocaleString()}</small>
                            </p>
                        )}
                        {account.complianceInquiryId && (
                            <p className='text-muted mb-2'>
                                <small>Inquiry ID: {account.complianceInquiryId}</small>
                            </p>
                        )}
                    </div>

                    {account.twilioStatus?.toLowerCase().includes('approved') && (
                        <MessageAlert
                            alert={{
                                variant: 'success',
                                messages: ['Your toll-free number has been verified and is ready for use.']
                            }}
                        />
                    )}

                    {account.twilioStatus?.toLowerCase().includes('rejected') && (
                        <MessageAlert
                            alert={{
                                variant: 'danger',
                                messages: ['Your toll-free verification was rejected. Please resubmit the compliance form with corrected information.']
                            }}
                        />
                    )}

                    {canEditCompliance(account.twilioStatus) && (
                        <DefaultButton
                            className='btn-lg btn-primary btnWidth mt-3'
                            variant='primary'
                            onClick={handleStartCompliance}
                            disabled={loading}
                        >
                            {getButtonText(account.twilioStatus)}
                        </DefaultButton>
                    )}
                </>
            ) : (
                <>
                    <div className='compliance-embed-container' style={{ height: '800px', width: '100%' }}>
                        {sessionData && (
                            <div style={{ width: '100%', height: '100%' }}>
                                <TwilioComplianceEmbed
                                    inquiryId={sessionData.inquiryId}
                                    inquirySessionToken={sessionData.sessionToken}
                                />
                            </div>
                        )}
                    </div>
                    <hr />
                    <DefaultButton
                        variant='outline-secondary'
                        className='btn-lg'
                        onClick={handleFormClose}
                    >
                        Close Form
                    </DefaultButton>
                </>
            )}
        </div>
    )
}

export default TwilioComplianceEmbeddable
